/** Facebook workspace — v2-FIRST feature (2026-07-26).
 *
 * Tom's account was blocked for 24h on 2026-07-25 for two things: identical
 * text posted across groups, and comments fired off too fast. This page exists
 * so the CRM makes both mistakes hard to repeat — it drafts copy from a local
 * model on his Mac, and it keeps score against the daily limits from his
 * playbook so he can see where he stands before he crosses a line.
 *
 * DELIBERATELY NOT AUTOMATED. There is no Facebook login here, no stored
 * credential, no posting, no API call to Meta. Two reasons: an account driven
 * by software is the single fastest route to a permanent disable (a hand-typed
 * duplicate costs a day; an automated account costs the account), and Meta has
 * no API for personal profiles or groups at all — only Pages — so there is no
 * compliant path even if we wanted one. Tom copies the draft and posts it
 * himself. That manual step is the whole point: it is what keeps the account
 * reading as a person.
 *
 * The counters live in this browser (localStorage), not the database: they're a
 * personal pace-keeper, not org data, and they must work even when the CRM
 * can't reach Supabase. */

export type FbAction = "comment" | "dm" | "group_post" | "timeline_post" | "group_join" | "friend_req";

export interface FbEntry {
  ts: number;
  action: FbAction;
  /** group name, person, or property — free text, whatever Tom typed */
  target: string;
  /** first words of what he actually sent, so duplicates are catchable */
  text: string;
}

export interface FbCap {
  label: string;
  /** the number the playbook calls comfortable for an established account */
  cap: number;
  /** what it cost him last time he blew past it */
  note: string;
}

export const FB_STORE = "crm2_fb_activity";
export const DAY_MS = 86_400_000;

/** Playbook caps (2026-07-25 research). Not Meta's published numbers — Meta
 * publishes none — but the conservative end of what practitioners report, and
 * far under where automated flags start. Velocity matters more than volume:
 * fifteen comments spread over a day is safe, five in ten minutes is not. */
export const FB_CAPS: Record<FbAction, FbCap> = {
  comment: { label: "Comments", cap: 15, note: "space them 3-5 min apart; bursts are what tripped the block" },
  dm: { label: "New DMs", cap: 10, note: "only to people whose post you're answering; never the same first line twice" },
  group_post: { label: "Group posts", cap: 3, note: "different wording per group, 20+ min apart, same group at most 1-2x/week" },
  timeline_post: { label: "Timeline posts", cap: 2, note: "your own wall; 1 is plenty" },
  group_join: { label: "Group joins", cap: 3, note: "10+ in a day triggers a joining-too-fast block" },
  friend_req: { label: "Friend requests", cap: 10, note: "only people you've actually interacted with; declines compound" },
};

export const ACTION_ORDER: FbAction[] = ["comment", "dm", "group_post", "timeline_post", "group_join", "friend_req"];

/* ------------------------------------------------------------- storage */

export function narrowEntries(raw: unknown): FbEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: FbEntry[] = [];
  for (const r of raw) {
    if (r === null || typeof r !== "object") continue;
    const o = r as Record<string, unknown>;
    const ts = typeof o["ts"] === "number" ? o["ts"] : NaN;
    const action = String(o["action"] ?? "");
    if (!Number.isFinite(ts) || !(action in FB_CAPS)) continue;
    out.push({
      ts,
      action: action as FbAction,
      target: typeof o["target"] === "string" ? o["target"] : "",
      text: typeof o["text"] === "string" ? o["text"] : "",
    });
  }
  return out.sort((a, b) => b.ts - a.ts);
}

export function loadEntries(): FbEntry[] {
  try {
    return narrowEntries(JSON.parse(localStorage.getItem(FB_STORE) ?? "[]"));
  } catch {
    return [];
  }
}

/** Keep 60 days — enough for "when did this group last see me", small enough
 * to never bloat storage. */
export function saveEntries(entries: FbEntry[], now: number): void {
  const keep = entries.filter((e) => now - e.ts < 60 * DAY_MS).slice(0, 2000);
  try { localStorage.setItem(FB_STORE, JSON.stringify(keep)); } catch { /* private mode */ }
}

/* ------------------------------------------------------------- counting */

/** Local calendar day, not a rolling 24h — matches how a person thinks about
 * "how much have I done today". */
export function isSameDay(a: number, b: number): boolean {
  const x = new Date(a); const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

export function todayCounts(entries: FbEntry[], now: number): Record<FbAction, number> {
  const out = { comment: 0, dm: 0, group_post: 0, timeline_post: 0, group_join: 0, friend_req: 0 };
  for (const e of entries) if (isSameDay(e.ts, now)) out[e.action] += 1;
  return out;
}

export type CapState = "ok" | "near" | "over";

export function capState(count: number, cap: number): CapState {
  if (count >= cap) return "over";
  if (count >= Math.ceil(cap * 0.7)) return "near";
  return "ok";
}

/** Minutes since the last action of this kind — the velocity half of the rule.
 * null when there isn't one yet. */
export function minutesSince(entries: FbEntry[], action: FbAction, now: number): number | null {
  const last = entries.find((e) => e.action === action);
  return last ? Math.floor((now - last.ts) / 60000) : null;
}

/** Comments want 3+ minutes between them, group posts 20+. Returns a warning
 * string when Tom is moving faster than that, or null when he's fine. */
export function paceWarning(entries: FbEntry[], action: FbAction, now: number): string | null {
  const gap = action === "comment" ? 3 : action === "group_post" ? 20 : action === "dm" ? 2 : 0;
  if (!gap) return null;
  const since = minutesSince(entries, action, now);
  if (since === null || since >= gap) return null;
  return `Last one was ${since} min ago — give it ${gap}+ min between ${FB_CAPS[action].label.toLowerCase()}.`;
}

/* -------------------------------------------------- duplicate protection */

const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ").trim();

/** Same 4-word-shingle test the drafting guardrails use. Catches "I reworded
 * two words and posted it to another group", which is exactly what the spam
 * system flags. */
export function overlap(a: string, b: string): number {
  const grams = (t: string): Set<string> => {
    const w = norm(t).split(" ").filter(Boolean);
    const g = new Set<string>();
    for (let i = 0; i + 4 <= w.length; i++) g.add(w.slice(i, i + 4).join(" "));
    return g;
  };
  const A = grams(a); const B = grams(b);
  if (!A.size || !B.size) return 0;
  let hit = 0;
  A.forEach((x) => { if (B.has(x)) hit++; });
  return hit / Math.min(A.size, B.size);
}

export const DUP_AT = 0.35;

/** Have I effectively sent this before? Looks back 14 days. */
export function findDuplicate(entries: FbEntry[], text: string, now: number): FbEntry | null {
  if (norm(text).split(" ").length < 5) return null;
  for (const e of entries) {
    if (now - e.ts > 14 * DAY_MS) break;
    if (overlap(e.text, text) > DUP_AT) return e;
  }
  return null;
}

/** Days since a named group last saw a post from Tom — the playbook wants 7+
 * for the same group. Case/spacing tolerant. */
export function daysSinceGroup(entries: FbEntry[], group: string, now: number): number | null {
  const want = norm(group);
  if (!want) return null;
  const hit = entries.find((e) => e.action === "group_post" && norm(e.target) === want);
  return hit ? Math.floor((now - hit.ts) / DAY_MS) : null;
}

export function groupWarning(entries: FbEntry[], group: string, now: number): string | null {
  const d = daysSinceGroup(entries, group, now);
  if (d === null) return null;
  if (d >= 7) return null;
  return `You posted to this group ${d === 0 ? "today" : d === 1 ? "yesterday" : `${d} days ago`} — the playbook says wait 7+ days.`;
}

/* ------------------------------------------------------------- summary */

export interface DayStatus {
  counts: Record<FbAction, number>;
  worst: CapState;
  overs: FbAction[];
}

export function dayStatus(entries: FbEntry[], now: number): DayStatus {
  const counts = todayCounts(entries, now);
  const overs: FbAction[] = [];
  let worst: CapState = "ok";
  for (const a of ACTION_ORDER) {
    const st = capState(counts[a], FB_CAPS[a].cap);
    if (st === "over") { overs.push(a); worst = "over"; }
    else if (st === "near" && worst === "ok") worst = "near";
  }
  return { counts, worst, overs };
}
